export const EVENT_TYPE_KIND = {
  ONE_ON_ONE: 'one_on_one',
  GROUP: 'group',
  ROUND_ROBIN: 'round_robin',
  COLLECTIVE: 'collective',
}

export const EVENT_TYPE_KIND_LABEL = {
  [EVENT_TYPE_KIND.ONE_ON_ONE]: 'One-on-one',
  [EVENT_TYPE_KIND.GROUP]: 'Group',
  [EVENT_TYPE_KIND.ROUND_ROBIN]: 'Round robin',
  [EVENT_TYPE_KIND.COLLECTIVE]: 'Collective',
}

export const EVENT_TYPE_KIND_COMING_SOON = [
  EVENT_TYPE_KIND.ROUND_ROBIN,
  EVENT_TYPE_KIND.COLLECTIVE,
]

export const CALENDAR_THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto',
}

export const LOCATION_KIND = {
  ZOOM: 'zoom',
  GOOGLE_MEET: 'google_meet',
  TEAMS: 'teams',
  PHONE: 'phone',
  IN_PERSON: 'in_person',
  CUSTOM: 'custom',
  ASK_INVITEE: 'ask_invitee',
}

export const LOCATION_LABEL = {
  [LOCATION_KIND.ZOOM]: 'Zoom',
  [LOCATION_KIND.GOOGLE_MEET]: 'Google Meet',
  [LOCATION_KIND.TEAMS]: 'Microsoft Teams',
  [LOCATION_KIND.PHONE]: 'Phone call',
  [LOCATION_KIND.IN_PERSON]: 'In-person meeting',
  [LOCATION_KIND.CUSTOM]: 'Custom link or location',
  [LOCATION_KIND.ASK_INVITEE]: 'Ask invitee',
}

export const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
  { value: 90, label: '90 minutes' },
  { value: 120, label: '2 hours' },
]

export const COLOR_OPTIONS = [
  {
    value: '#5C33FF',
    label: 'CodeDesign Purple',
    hex: '#5C33FF',
    legacyValue: 'codedesign',
  },
  { value: '#6366F1', label: 'Indigo', hex: '#6366F1', legacyValue: 'indigo' },
  { value: '#3B82F6', label: 'Blue', hex: '#3B82F6', legacyValue: 'blue' },
  {
    value: '#10B981',
    label: 'Emerald',
    hex: '#10B981',
    legacyValue: 'emerald',
  },
  { value: '#F43F5E', label: 'Rose', hex: '#F43F5E', legacyValue: 'rose' },
  { value: '#F59E0B', label: 'Amber', hex: '#F59E0B', legacyValue: 'amber' },
  { value: '#18181B', label: 'Slate', hex: '#18181B', legacyValue: 'zinc' },
]

export function resolveCalendarColor(value, fallback = '#5C33FF') {
  if (typeof value !== 'string') return fallback
  const trimmed = value.trim()
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(trimmed)) return trimmed
  if (/^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(trimmed)) return `#${trimmed}`
  const match = COLOR_OPTIONS.find(
    (c) => c.legacyValue === trimmed || c.value === trimmed
  )
  return match?.hex || fallback
}

export function getRandomCalendarColor() {
  return COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)]?.hex || '#5C33FF'
}

export const BUFFER_OPTIONS = [
  { value: 0, label: 'No buffer' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '60 minutes' },
]

export const MIN_NOTICE_OPTIONS = [
  { value: 0, label: 'No minimum' },
  { value: 60, label: '1 hour' },
  { value: 240, label: '4 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '1 day' },
  { value: 2880, label: '2 days' },
]

export const HORIZON_OPTIONS = [
  { value: 7, label: '7 days into the future' },
  { value: 14, label: '14 days into the future' },
  { value: 30, label: '30 days into the future' },
  { value: 60, label: '60 days into the future' },
  { value: 90, label: '90 days into the future' },
]

export const QUESTION_KIND = {
  TEXT: 'text',
  PARAGRAPH: 'paragraph',
  EMAIL: 'email',
  PHONE: 'phone',
  SINGLE_CHOICE: 'single_choice',
  MULTI_CHOICE: 'multi_choice',
}

export const QUESTION_KIND_LABEL = {
  [QUESTION_KIND.TEXT]: 'Short answer',
  [QUESTION_KIND.PARAGRAPH]: 'Long answer',
  [QUESTION_KIND.EMAIL]: 'Email',
  [QUESTION_KIND.PHONE]: 'Phone number',
  [QUESTION_KIND.SINGLE_CHOICE]: 'Single choice',
  [QUESTION_KIND.MULTI_CHOICE]: 'Multiple choice',
}

export const BOOKING_STATUS = {
  CONFIRMED: 'confirmed',
  PENDING: 'pending',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  NO_SHOW: 'no_show',
}

export const BOOKING_STATUS_LABEL = {
  [BOOKING_STATUS.CONFIRMED]: 'Confirmed',
  [BOOKING_STATUS.PENDING]: 'Pending approval',
  [BOOKING_STATUS.CANCELLED]: 'Cancelled',
  [BOOKING_STATUS.COMPLETED]: 'Completed',
  [BOOKING_STATUS.NO_SHOW]: 'No show',
}

export const BOOKING_STATUS_BADGE_COLOR = {
  [BOOKING_STATUS.CONFIRMED]: 'green',
  [BOOKING_STATUS.PENDING]: 'amber',
  [BOOKING_STATUS.CANCELLED]: 'zinc',
  [BOOKING_STATUS.COMPLETED]: 'blue',
  [BOOKING_STATUS.NO_SHOW]: 'red',
}

export const WEEKDAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
]

const FALLBACK_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — Los Angeles' },
  { value: 'America/Denver', label: 'Mountain Time — Denver' },
  { value: 'America/Chicago', label: 'Central Time — Chicago' },
  { value: 'America/New_York', label: 'Eastern Time — New York' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Berlin', label: 'Central European — Berlin' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Kolkata', label: 'India — Kolkata' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
]

export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

function getTimezoneOffsetLabel(timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date())
    return parts.find((part) => part.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

function formatTimezoneLabel(timezone) {
  if (timezone === 'UTC') return 'UTC (Coordinated Universal Time)'
  const offset = getTimezoneOffsetLabel(timezone)
  const readableName = timezone.replace(/_/g, ' ')
  return offset ? `${readableName} (${offset})` : readableName
}

function getSupportedTimezones() {
  if (typeof Intl.supportedValuesOf !== 'function') return FALLBACK_TIMEZONES

  const browserTimezone = getBrowserTimezone()
  const values = ['UTC', browserTimezone, ...Intl.supportedValuesOf('timeZone')]
  const uniqueValues = [...new Set(values.filter(Boolean))]

  return uniqueValues.map((timezone) => ({
    value: timezone,
    label: formatTimezoneLabel(timezone),
  }))
}

export const TIMEZONES = getSupportedTimezones()

export const REMINDER_OFFSETS = [
  { value: 15, label: '15 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 1440, label: '24 hours before' },
  { value: 2880, label: '2 days before' },
  { value: 10080, label: '1 week before' },
]

export const INTEGRATION_PROVIDERS = [
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    category: 'calendar',
    description: 'Check availability and add bookings to Google Calendar.',
  },
  {
    id: 'outlook_calendar',
    name: 'Outlook Calendar',
    category: 'calendar',
    description:
      'Check Microsoft calendar conflicts and power Teams meeting links.',
  },
  // {
  //   id: 'icloud_calendar',
  //   name: 'iCloud Calendar',
  //   category: 'calendar',
  //   description: 'Sync availability with your Apple iCloud calendar.',     //TODO: IMPLEMENT LATER!!!
  // },
  {
    id: 'zoom',
    name: 'Zoom',
    category: 'video',
    description: 'Auto-create Zoom meeting links for bookings.',
    comingSoon: true,
  },
  {
    id: 'google_meet',
    name: 'Google Meet',
    category: 'video',
    description: 'Generate Google Meet links automatically.',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    category: 'video',
    description:
      'Generate Teams meeting links through the connected Outlook Calendar account.',
  },
  {
    id: 'webhooks',
    name: 'Outgoing webhooks',
    category: 'developer',
    description: 'Send booking events to your own endpoints.',
  },
]

export const INTEGRATION_CATEGORY_LABEL = {
  calendar: 'Calendar conflicts',
  video: 'Video conferencing',
  developer: 'Developer',
}

export const DEFAULT_AVAILABILITY = {
  monday: [{ from: '09:00', to: '17:00' }],
  tuesday: [{ from: '09:00', to: '17:00' }],
  wednesday: [{ from: '09:00', to: '17:00' }],
  thursday: [{ from: '09:00', to: '17:00' }],
  friday: [{ from: '09:00', to: '17:00' }],
  saturday: [],
  sunday: [],
}
